import React, { useCallback, useMemo } from 'react';
import {
  ErrorBoundary as ReactErrorBoundary,
  FallbackProps,
} from 'react-error-boundary';
import { ApolloError } from '@apollo/client';
import ErrorBoundaryContext, {
  useErrorBoundaryContext,
} from './contexts/ErrorBoundaryContext';

import ErrorMessageFull from './ErrorMessageFull';
import ErrorMessageLite from './ErrorMessageLite';
import { useAppLoading } from '#core/AppLoading';
import useLogger from '#hooks/useLogger';

export type { FallbackProps } from 'react-error-boundary';

type ExtendedFallbackProps = FallbackProps & {
  id: string;
  footerComponent?: React.FC<{}>;
};

type OnErrorHandler = (error: Error, info: { componentStack: string }) => void;

type ErrorBoundaryCommonProps = {
  id: string;
  onError?: OnErrorHandler;
  footerComponent?: React.FC<{}>;
};
type ErrorBoundaryWithErrorComponent = ErrorBoundaryCommonProps & {
  ErrorComponent: React.FC<
    FallbackProps & {
      id: string;
      footerComponent?: React.FC<{}>;
    }
  >;
};
type ErrorBoundaryWithFullOrLiteMessage = ErrorBoundaryCommonProps & {
  showDetailedErrorScreen?: boolean;
};
type ErrorBoundaryProps =
  | ErrorBoundaryWithErrorComponent
  | ErrorBoundaryWithFullOrLiteMessage;

const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({
  children,
  onError: onErrorProp,
  footerComponent,
  id: thisBoundaryId,
  ...props
}) => {
  const logger = useLogger();
  const { crumbs: errorCrumbs } = useErrorBoundaryContext();
  const newErrorCrumbs = [...errorCrumbs, thisBoundaryId];
  const id = newErrorCrumbs.join('/');
  const { isLoading, finishedLoading } = useAppLoading();

  const ErrorComponent = (props as ErrorBoundaryWithErrorComponent)
    ?.ErrorComponent;
  const showDetailedErrorScreen = (props as ErrorBoundaryWithFullOrLiteMessage)
    ?.showDetailedErrorScreen;

  const FallbackComponent = useMemo(() => {
    if (typeof ErrorComponent !== 'undefined') {
      return ErrorComponent;
    }
    if (showDetailedErrorScreen === true) {
      return ErrorMessageFull;
    }
    return ErrorMessageLite;
  }, [ErrorComponent, showDetailedErrorScreen]) as React.FC<
    ExtendedFallbackProps
  >;

  const onError: OnErrorHandler = useCallback(
    (error, info) => {
      if (isLoading) {
        finishedLoading();
      }
      if (error instanceof ApolloError) {
        const apolloError = error as ApolloError;
        const errorType = apolloError.networkError
          ? 'GraphQL network'
          : 'Apollo';
        logger.error(
          `Error boundary for ID '${id}' encountered an ${errorType} error`,
        );
      } else {
        logger.error(`Error boundary for ID '${id}' encountered an error`);
      }
      logger.error(error);
      if (typeof onErrorProp === 'function') {
        onErrorProp(error, info);
      }
    },
    [isLoading, logger, onErrorProp, finishedLoading, id],
  );

  return (
    <ErrorBoundaryContext.Provider
      data-testid="error"
      value={{
        crumbs: newErrorCrumbs,
      }}
    >
      <ReactErrorBoundary
        FallbackComponent={({ error, resetErrorBoundary }) => (
          <FallbackComponent
            id={id}
            error={error}
            resetErrorBoundary={resetErrorBoundary}
            footerComponent={footerComponent}
          />
        )}
        onError={onError}
      >
        {children}
      </ReactErrorBoundary>
    </ErrorBoundaryContext.Provider>
  );
};

export default ErrorBoundary;
